// Alias-binding signature verification
//
// The edge wrapper HTML carries four meta tags:
//
//   <meta name="p2claw-peer-id"           content="...">
//   <meta name="p2claw-alias"             content="blue-otter-7392">
//   <meta name="p2claw-binding-sig"       content="<base64url, 64 bytes>">
//   <meta name="p2claw-binding-issued-at" content="1767312000">
//
// The binding sig is coord's root-key Ed25519 signature over
// `{alias, peer_id, issued_at}`. The browser pins coord's root pubkey
// at build time (`__P2CLAW_ROOT_PUBKEY__` in `vite.config.ts`) and
// MUST verify this binding before trusting the peer_id it just read
// from the edge-served HTML. Without this check, a malicious edge
// could hand us an arbitrary peer_id bound to any alias it likes;
// with it, the only thing a compromised edge can do is refuse to
// serve at all.
//
// Byte layout (mirrors `build_alias_binding_payload` in
// `crates/identity/src/proofs.rs`):
//
//   "p2claw-binding-v1" || 0x0A
//       || u16_be(len(alias)) || alias (UTF-8)
//       || 0x00                           ; inner separator; reserves
//                                         ; room for a future `kind`
//                                         ; byte without retroactively
//                                         ; changing older signatures.
//       || pubkey (32 bytes)
//       || u64_be(issued_at)
//
// Primary verification path: native WebCrypto `Ed25519`. Modern
// browsers (Chrome ≥ 113, Safari ≥ 17, Firefox ≥ 129) all ship it.
//
// Fallback path: on browsers without native Ed25519
// (Safari 15/16, anything older than the cutoff) we dynamic-import
// `@noble/ed25519` and verify in JS. ~5 KB gzip; Vite splits it into
// its own chunk by default, so modern browsers never download it.
// Trust delta is negligible for verification — no private key
// material is involved, the signed payload is public, and
// `@noble/ed25519` is widely audited.

/** Exact domain-separator bytes baked into the signed payload. */
export const ALIAS_BINDING_DOMAIN_SEP = "p2claw-binding-v1";

/** Length byte that separates the domain-separator prefix from the body. */
export const ALIAS_BINDING_SEPARATOR_BYTE = 0x0a;

/** Inner zero-byte separator between the length-prefixed alias and pubkey. */
export const ALIAS_BINDING_INNER_SEPARATOR = 0x00;

/**
 * Build the canonical byte payload that gets signed / verified.
 *
 * Mirrors `build_alias_binding_payload` in
 * `crates/identity/src/proofs.rs`.
 */
export function buildAliasBindingPayload(input: {
  alias: string;
  peerIdBytes: Uint8Array; // 32 bytes
  /**
   * Unix seconds; serialised as u64 big-endian on the wire. Accept
   * `number` (always safe for real timestamps — far below 2^53) and
   * `bigint` (for cross-language golden-vector tests that exercise
   * the full u64 range).
   */
  issuedAt: number | bigint;
}): Uint8Array {
  const { alias, peerIdBytes, issuedAt } = input;
  if (peerIdBytes.length !== 32) {
    throw new Error(
      `binding: peer_id must be 32 bytes (got ${peerIdBytes.length})`,
    );
  }
  const issuedAtBig = toU64(issuedAt);
  const sepBytes = new TextEncoder().encode(ALIAS_BINDING_DOMAIN_SEP);
  const aliasBytes = new TextEncoder().encode(alias);
  if (aliasBytes.length > 0xffff) {
    throw new Error(`binding: alias too long (${aliasBytes.length} bytes)`);
  }

  const total = sepBytes.length + 1 + 2 + aliasBytes.length + 1 + 32 + 8;
  const out = new Uint8Array(total);
  let p = 0;
  out.set(sepBytes, p);
  p += sepBytes.length;
  out[p++] = ALIAS_BINDING_SEPARATOR_BYTE;
  out[p++] = (aliasBytes.length >>> 8) & 0xff;
  out[p++] = aliasBytes.length & 0xff;
  out.set(aliasBytes, p);
  p += aliasBytes.length;
  out[p++] = ALIAS_BINDING_INNER_SEPARATOR;
  out.set(peerIdBytes, p);
  p += 32;
  // u64_be(issued_at).
  const mask8 = 0xffn;
  for (let i = 7; i >= 0; i--) {
    out[p++] = Number((issuedAtBig >> BigInt(i * 8)) & mask8);
  }
  return out;
}

/**
 * Coerce a `number | bigint` timestamp to a canonical unsigned
 * 64-bit `bigint`. Throws on negative input, non-finite numbers, or
 * values out of u64 range.
 */
function toU64(v: number | bigint): bigint {
  let big: bigint;
  if (typeof v === "bigint") {
    big = v;
  } else {
    if (!Number.isFinite(v)) {
      throw new Error(`binding: issued_at must be finite (got ${v})`);
    }
    if (!Number.isSafeInteger(v)) {
      throw new Error(
        `binding: issued_at must be a safe integer (got ${v}); pass a bigint for values > 2^53-1`,
      );
    }
    big = BigInt(v);
  }
  if (big < 0n || big > 0xffff_ffff_ffff_ffffn) {
    throw new Error(`binding: issued_at out of u64 range (got ${String(big)})`);
  }
  return big;
}

/**
 * Decode a base64url (RFC 4648 §5) string without padding. Accepts
 * padded forms too for robustness. Returns `null` if the string
 * contains any character outside the URL-safe alphabet or has a
 * length that can't produce a whole byte count.
 */
export function decodeBase64url(input: string): Uint8Array | null {
  if (input.length === 0) return new Uint8Array(0);
  // Strip any trailing padding — base64url is usually pad-free but
  // accept the padded form for input robustness.
  let s = input.replace(/=+$/, "");
  if (!/^[A-Za-z0-9_-]+$/.test(s)) return null;
  // Re-pad to a multiple of 4 so atob can digest it.
  const rem = s.length % 4;
  if (rem === 1) return null; // impossible length for any valid encoding
  if (rem > 0) s += "=".repeat(4 - rem);
  // Convert URL-safe chars back to standard base64.
  s = s.replace(/-/g, "+").replace(/_/g, "/");
  // `atob` exists in the browser and in Node 16+; the bootstrap bundle
  // targets ES2022 so this is always available.
  try {
    const bin = atob(s);
    const out = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
    return out;
  } catch {
    return null;
  }
}

/** Tagged verification result.
 *
 *   * `{ ok: true, via: "native" }`     — modern browser, native
 *                                          WebCrypto verified.
 *   * `{ ok: true, via: "fallback" }`   — old browser, noble verified.
 *   * `{ ok: false, reason: "invalid" }`— signature didn't match. At
 *                                         least one verifier ran
 *                                         end-to-end and rejected.
 *                                         Surface the existing
 *                                         "binding_sig_invalid" copy.
 *   * `{ ok: false, reason: "unsupported" }` — neither native nor
 *                                              fallback could run
 *                                              (e.g. fallback chunk
 *                                              failed to load on a
 *                                              completely-broken
 *                                              browser, no JS
 *                                              dynamic-import
 *                                              support, etc.).
 *                                              Surface a "browser
 *                                              too old" message.
 */
export type BindingVerifyResult =
  | { ok: true; via: "native" | "fallback" }
  | { ok: false; reason: "invalid" | "unsupported" };

/** Subset of `@noble/ed25519` we use. Defined as a structural type
 *  so tests can inject a stub without pulling the real module in. */
export interface NobleEd25519 {
  verifyAsync(
    signature: Uint8Array,
    payload: Uint8Array,
    rootPubkey: Uint8Array,
  ): Promise<boolean>;
}

export interface VerifyAliasBindingInput {
  alias: string;
  peerIdBytes: Uint8Array;
  issuedAt: number | bigint;
  /** 64 bytes, base64url-encoded (no padding). */
  signatureB64url: string;
  /** 32 bytes, the pinned coord-root Ed25519 public key. */
  rootPubkeyBytes: Uint8Array;
  /**
   * Optional SubtleCrypto override (lets tests pin a specific
   * implementation; prod reads `globalThis.crypto.subtle`).
   */
  subtle?: SubtleCrypto;
  /**
   * Called once when native is unsupported and we're about to load
   * the noble fallback. The caller renders a "Loading
   * compatibility…" overlay; `verifyAliasBinding`'s resolution
   * implicitly signals to hide it. Not invoked when native
   * succeeds — modern browsers never see this. */
  onFallbackStart?: () => void;
  /**
   * Test seam — substitute the noble loader. Defaults to the
   * dynamic `import("@noble/ed25519")` Vite splits into its own
   * chunk so the bundle isn't paid by modern browsers.
   */
  loadFallback?: () => Promise<NobleEd25519>;
}

/**
 * Verify a coord-signed alias → peer_id binding against a pinned
 * root public key. See `BindingVerifyResult` for return shape.
 *
 * Caller maps:
 *   * `{ ok: true, via: "native" }`     — silent boot.
 *   * `{ ok: true, via: "fallback" }`   — silent boot (the wrapper
 *                                         has already shown the
 *                                         loading overlay via
 *                                         `onFallbackStart`; it
 *                                         takes the resolution as
 *                                         the cue to hide).
 *   * `{ ok: false, reason: "invalid" }`— the existing scary
 *                                         `binding_sig_invalid` copy.
 *                                         At least one verifier
 *                                         actually ran; this is a
 *                                         real signature mismatch
 *                                         or MITM, not an old
 *                                         browser.
 *   * `{ ok: false, reason: "unsupported" }` — "your browser is too
 *                                              old to verify
 *                                              identity, and the
 *                                              compatibility fallback
 *                                              didn't load either."
 */
export async function verifyAliasBinding(
  input: VerifyAliasBindingInput,
): Promise<BindingVerifyResult> {
  const { alias, peerIdBytes, issuedAt, signatureB64url, rootPubkeyBytes } = input;

  if (peerIdBytes.length !== 32) return { ok: false, reason: "invalid" };
  if (rootPubkeyBytes.length !== 32) return { ok: false, reason: "invalid" };

  const signature = decodeBase64url(signatureB64url);
  if (!signature || signature.length !== 64) return { ok: false, reason: "invalid" };

  let payload: Uint8Array;
  try {
    payload = buildAliasBindingPayload({ alias, peerIdBytes, issuedAt });
  } catch {
    // Oversized alias / invalid issued_at — treat as verification
    // failure, not a programmer error.
    return { ok: false, reason: "invalid" };
  }

  // Native path: try WebCrypto Ed25519. If `importKey` throws, the
  // browser doesn't support Ed25519 (Safari < 17, Firefox < 129,
  // Chrome < 113) and we fall through to the noble path.
  const subtle = input.subtle ?? getSubtleOrNull();
  if (subtle !== null) {
    let nativeKey: CryptoKey | null = null;
    try {
      const keyBuf = new ArrayBuffer(rootPubkeyBytes.byteLength);
      new Uint8Array(keyBuf).set(rootPubkeyBytes);
      nativeKey = await subtle.importKey(
        "raw",
        keyBuf,
        { name: "Ed25519" },
        false,
        ["verify"],
      );
    } catch {
      // `importKey` throws either NotSupportedError (Ed25519
      // algorithm unknown) or DataError (key bytes malformed). The
      // latter is rare — we already validated length above — so any
      // error here is overwhelmingly the unsupported case. Fall
      // through to noble.
      nativeKey = null;
    }
    if (nativeKey !== null) {
      try {
        const sigBuf = new ArrayBuffer(signature.byteLength);
        new Uint8Array(sigBuf).set(signature);
        const payBuf = new ArrayBuffer(payload.byteLength);
        new Uint8Array(payBuf).set(payload);
        const ok = await subtle.verify({ name: "Ed25519" }, nativeKey, sigBuf, payBuf);
        return ok ? { ok: true, via: "native" } : { ok: false, reason: "invalid" };
      } catch {
        // `verify` itself threw — treat as `invalid`. Native ran far
        // enough that a falsy return would have been a clean
        // mismatch; a thrown error here usually means corrupted
        // input, not an unsupported browser.
        return { ok: false, reason: "invalid" };
      }
    }
  }

  // Fallback path. Native couldn't import the key (or `subtle` is
  // entirely unavailable on this platform). Hand control to the
  // caller via `onFallbackStart` so they can render the loading
  // overlay, then dynamic-import noble.
  input.onFallbackStart?.();
  const loader = input.loadFallback ?? defaultNobleLoader;
  let noble: NobleEd25519;
  try {
    noble = await loader();
  } catch {
    // No native, no fallback — true "unsupported". Caller surfaces
    // a "browser too old" message.
    return { ok: false, reason: "unsupported" };
  }
  try {
    const ok = await noble.verifyAsync(signature, payload, rootPubkeyBytes);
    return ok ? { ok: true, via: "fallback" } : { ok: false, reason: "invalid" };
  } catch {
    // Noble's `verifyAsync` throws on malformed inputs (e.g. wrong
    // signature length — which we pre-validated, so this branch is
    // largely defensive). Treat as `invalid`.
    return { ok: false, reason: "invalid" };
  }
}

/** Default loader. Dynamic-import lets Vite split @noble/ed25519
 *  into its own chunk that only old-browser sessions download. */
const defaultNobleLoader = (): Promise<NobleEd25519> =>
  import("@noble/ed25519") as Promise<NobleEd25519>;

/** Returns `globalThis.crypto.subtle` or `null` if WebCrypto is
 *  entirely unavailable. The completely-no-WebCrypto case is rare
 *  but possible (older Safari over insecure origin, exotic
 *  embedded environments); we drop straight to the fallback path
 *  rather than throwing. */
function getSubtleOrNull(): SubtleCrypto | null {
  const c = (globalThis as { crypto?: Crypto }).crypto;
  return c?.subtle ?? null;
}
